import { useQuery, useMutation, useQueryClient } from 'react-query';
import { message } from 'antd';
import { doctorsApi } from '../api';

// Query keys
export const PARTNER_DOCTORS_QUERY_KEY = 'partner-doctors';
export const AVAILABLE_DOCTORS_QUERY_KEY = 'available-doctors';

// Get all doctors associated with a partner
export const usePartnerDoctors = (partnerId, params = {}, options = {}) => {
  return useQuery(
    [PARTNER_DOCTORS_QUERY_KEY, partnerId, params],
    () => doctorsApi.getByPartner(partnerId, params),
    {
      enabled: !!partnerId,
      staleTime: 30000,
      ...options,
    }
  );
};

// Get available doctors (not associated with partner)
export const useAvailableDoctors = (partnerId, params = {}, options = {}) => {
  return useQuery(
    [AVAILABLE_DOCTORS_QUERY_KEY, partnerId, params],
    () => doctorsApi.getAvailable(partnerId, params),
    {
      enabled: !!partnerId,
      staleTime: 30000,
      ...options,
    }
  );
};

// Search doctors
export const useSearchDoctors = (keyword, options = {}) => {
  return useQuery(
    ['doctors-search', keyword],
    () => doctorsApi.search(keyword),
    {
      enabled: !!keyword && keyword.length >= 2,
      staleTime: 10000,
      ...options,
    }
  );
};

// Associate doctors with partner mutation
export const useAssociateDoctors = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ partnerId, doctorIds }) => doctorsApi.associate(partnerId, doctorIds),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([PARTNER_DOCTORS_QUERY_KEY, variables.partnerId]);
        queryClient.invalidateQueries([AVAILABLE_DOCTORS_QUERY_KEY, variables.partnerId]);
        message.success(
          `${variables.doctorIds.length} doctor(s) associated successfully`
        );
        return data;
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to associate doctors';
        message.error(errorMessage);
      },
    }
  );
};

// Dissociate doctor from partner mutation
export const useDissociateDoctor = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ partnerId, doctorId }) => doctorsApi.dissociate(partnerId, doctorId),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([PARTNER_DOCTORS_QUERY_KEY, variables.partnerId]);
        queryClient.invalidateQueries([AVAILABLE_DOCTORS_QUERY_KEY, variables.partnerId]);
        message.success('Doctor dissociated successfully');
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to dissociate doctor';
        message.error(errorMessage);
      },
    }
  );
};

// Bulk associate doctors mutation
export const useBulkAssociateDoctors = () => {
  const queryClient = useQueryClient();

  return useMutation(
    ({ partnerId, doctorIds }) => doctorsApi.bulkAssociate(partnerId, doctorIds),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([PARTNER_DOCTORS_QUERY_KEY, variables.partnerId]);
        queryClient.invalidateQueries([AVAILABLE_DOCTORS_QUERY_KEY, variables.partnerId]);
        message.success(
          `${variables.doctorIds.length} doctor(s) associated successfully`
        );
        return data;
      },
      onError: (error) => {
        const errorMessage = error.response?.data?.message || 'Failed to associate doctors';
        message.error(errorMessage);
      },
    }
  );
};